import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
} from '@mui/material'
import { Check, Star } from '@mui/icons-material'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import type { SubscriptionPlanDto, UserSubscriptionDto } from '@api/types/subscription'

type BillingCycle = 'monthly' | 'annual'

export function PlanComparisonSection() {
  const [plans, setPlans] = useState<SubscriptionPlanDto[]>([])
  const [currentSubscription, setCurrentSubscription] = useState<UserSubscriptionDto | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const [plansData, subscriptionData] = await Promise.all([
        subscriptionsApi.getPlans(),
        subscriptionsApi.getCurrentSubscription(),
      ])
      setPlans(plansData)
      setCurrentSubscription(subscriptionData)
    } catch (err) {
      setError('Failed to load subscription plans')
      console.error('Error loading plans:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleBillingCycleChange = (_: React.MouseEvent<HTMLElement>, newValue: BillingCycle | null) => {
    if (newValue) {
      setBillingCycle(newValue)
    }
  }

  const getPrice = (plan: SubscriptionPlanDto) => {
    if (plan.isFreeTier) return plan.formattedMonthlyPrice
    if (billingCycle === 'annual' && plan.annualPrice !== null) {
      return plan.formattedAnnualPrice
    }
    return plan.formattedMonthlyPrice
  }

  const getPriceSubtext = (plan: SubscriptionPlanDto) => {
    if (plan.isFreeTier) return 'Forever free'
    if (billingCycle === 'annual' && plan.annualPrice !== null) {
      const monthlyEquivalent = plan.annualPrice / 12
      const savings = ((plan.monthlyPrice * 12 - plan.annualPrice) / (plan.monthlyPrice * 12)) * 100
      return `$${monthlyEquivalent.toFixed(2)}/month (Save ${Math.round(savings)}%)`
    }
    return 'per month'
  }

  const isCurrentPlan = (plan: SubscriptionPlanDto) => {
    return currentSubscription?.plan.id === plan.id
  }

  const canUpgrade = (plan: SubscriptionPlanDto) => {
    if (!currentSubscription) return !plan.isFreeTier
    if (currentSubscription.plan.isFreeTier) return !plan.isFreeTier
    return false
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  // Check if any plan has annual pricing
  const hasAnnualPricing = plans.some((plan) => plan.annualPrice !== null)

  return (
    <Box>
      {/* Billing cycle toggle */}
      {hasAnnualPricing && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
          <ToggleButtonGroup
            value={billingCycle}
            exclusive
            onChange={handleBillingCycleChange}
            size="small"
          >
            <ToggleButton value="monthly">Monthly</ToggleButton>
            <ToggleButton value="annual">
              Annual
              <Chip label="Save" size="small" color="success" sx={{ ml: 1 }} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {/* Plans grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.min(plans.length, 3)}, 1fr)` },
          gap: 3,
          pt: 2, // Add padding top to make room for the "Current Plan" chip
        }}
      >
        {plans.map((plan) => {
          const isCurrent = isCurrentPlan(plan)
          const showUpgrade = canUpgrade(plan)

          return (
            <Card
              key={plan.id}
              elevation={isCurrent ? 4 : 1}
              sx={{
                position: 'relative',
                overflow: 'visible', // Allow the chip to overflow outside the card
                border: isCurrent ? '2px solid' : '1px solid',
                borderColor: isCurrent ? 'primary.main' : 'divider',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Current plan badge */}
              {isCurrent && (
                <Chip
                  icon={<Star fontSize="small" />}
                  label="Current Plan"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1,
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1, pt: isCurrent ? 4 : 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                  {plan.displayName}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {plan.quickDescription}
                </Typography>

                {/* Price */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h3" component="span" sx={{ fontWeight: 700 }}>
                    {getPrice(plan)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getPriceSubtext(plan)}
                  </Typography>
                </Box>

                {/* Trial period badge */}
                {plan.trialPeriodDays > 0 && !isCurrent && (
                  <Chip
                    label={`${plan.trialPeriodDays}-day free trial`}
                    color="info"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                )}

                {/* Features list from HTML description */}
                {plan.fullHtmlDescription && (
                  <Box
                    sx={{
                      '& ul': {
                        listStyle: 'none',
                        padding: 0,
                        margin: 0,
                      },
                      '& li': {
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        mb: 1,
                        '&::before': {
                          content: '""',
                        },
                      },
                    }}
                  >
                    <Stack spacing={1}>
                      {/* Parse HTML and render features with check icons */}
                      <div
                        dangerouslySetInnerHTML={{
                          __html: plan.fullHtmlDescription.replace(
                            /<li>/g,
                            '<li style="display: flex; align-items: flex-start; gap: 8px;"><svg style="flex-shrink: 0; margin-top: 2px; width: 20px; height: 20px; fill: currentColor; color: #4caf50;" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"></path></svg>'
                          ),
                        }}
                      />
                    </Stack>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ p: 2, pt: 0 }}>
                {isCurrent ? (
                  <Button fullWidth variant="outlined" disabled>
                    Current Plan
                  </Button>
                ) : showUpgrade ? (
                  <Button fullWidth variant="contained" color="primary">
                    {plan.trialPeriodDays > 0 ? 'Start Free Trial' : 'Upgrade'}
                  </Button>
                ) : (
                  <Button fullWidth variant="outlined" disabled>
                    {plan.isFreeTier ? 'Downgrade' : 'Select'}
                  </Button>
                )}
              </CardActions>
            </Card>
          )
        })}
      </Box>
    </Box>
  )
}
